import { AvailableDate, EHR, OmniPatient, Patient, RawWebptPatient, WaitlistPatient, WebptPatient } from "../types";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import { convertToCallablePhoneNumber } from "../utils/utils";
import { first } from "lodash";

interface CreatePatientParams {
  token: string;
  organizationId: string;
  selectedWebptPatient: RawWebptPatient;
  selectedCaseStudy: number | undefined;
  selectedProviders: string[];
  duration: number;
  phoneNumber: string;
  preferredName: string | undefined;
}

export const getAssignedProvider = async (patientId: number, token: string, organizationId: string) => {
  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/api/webpt/assigned-therapist/${patientId}?organizationId=${organizationId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data?.providers || [];
};

export const getCaseStudies = async (patientId: number, token: string, organizationId: string, firstName?: string, lastName?: string) => {
  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/api/webpt/patients/${patientId}?organizationId=${organizationId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { firstName, lastName },
    }
  );
  return response.data || [];
};

export const generateAvailableDates = (daysAhead: number = 90): AvailableDate[] => {
  const start = DateTime.now();
  const end = start.plus({ days: daysAhead - 1 });
  return [{ startDate: start.toISO()!, endDate: end.toISO()! }];
};

export const createPatientData = ({
  token,
  organizationId,
  selectedWebptPatient,
  selectedCaseStudy,
  selectedProviders,
  duration,
  phoneNumber,
  preferredName,
}: CreatePatientParams): OmniPatient => {
  const patientId = uuidv4();

  const patient: Patient = {
    patientId,
    organizationId,
    firstName: selectedWebptPatient.firstName,
    lastName: selectedWebptPatient.lastName,
    preferredName: preferredName,
    phoneNumber: convertToCallablePhoneNumber(phoneNumber),
    timezone: DateTime.local().zoneName,
    language: "en",
    callEnabled: false,
    textEnabled: true,
    dob: selectedWebptPatient.dob,
    consentHistory: [],
  };

  const waitlistPatient: WaitlistPatient = {
    patientId,
    organizationId,
    priority: 1,
    availableDates: generateAvailableDates(),
    patientGroupId: undefined,
  };

  const webptPatient: WebptPatient = {
    patientId,
    organizationId,
    webptPatientId: selectedWebptPatient.webptPatientId,
    webptProviderIds: selectedProviders,
    webptAppointmentDuration: duration,
    webptCaseStudyId: selectedCaseStudy || 0,
  };

  return {
    patientId,
    patient,
    waitlist: waitlistPatient,
    [EHR.webpt]: webptPatient,
  };
}; 